import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { company } from '~/company/Company';
import { CategoriesSubheader } from '~/components/HeaderV3/components/CategoriesSubheader';
import { LoyaltyChip } from '~/components/HeaderV3/components/LoyaltyChip';
import Icon from '~/components/Icon/Icon';
import HeaderLanguageChip from '~/components/LanguageSelector/HeaderLanguageChip';
import { useStatusBarControl } from '~/hooks/useStatusBarControl';
import { CONTENT_ID } from '~/pages/Root/constants';
import { mainStore } from '~/stores';

import { HeaderAddressWrapper } from './components/HeaderAddressWrapper';
import { HeaderDeliverySwitcher } from './components/HeaderDeliverySwitcher';
import { HeaderFavs } from './components/HeaderFavs';
import { HeaderOrders } from './components/HeaderOrders';
import { VIPExpressBanner } from './components/VIPExpressBanner';
import styles from './MobileHeaderCD.module.scss';

type MobileHeaderCDProps = Omit<
  React.ComponentPropsWithRef<'header'>,
  'children'
>;

const MobileHeaderCD = React.forwardRef<
  HTMLHeadingElement,
  MobileHeaderCDProps
>((props, ref) => {
  const { t } = useTranslation();
  const [transitionRunning, setTransitionRunning] = useState(false);

  const isOverlayExists = mainStore.isOverlayExists();

  const overlayExistsValueRef = useRef<Nullable<boolean>>(null);

  // Control status bar color based on scroll position. Only mcosmetic runs the
  // new app build that reliably honors web-driven status-bar styling; every
  // other tenant stays on the master behavior for the old app.
  useStatusBarControl({
    initialColor: company.match({
      mcosmetic: 'dark-content',
      cerise: 'dark-content',
      default: 'light-content',
    }),
    scrolledColor: 'dark-content',
  });

  const onTransitionEnd = () => {
    setTransitionRunning(false);
  };

  useEffect(() => {
    if (isOverlayExists !== overlayExistsValueRef.current) {
      if (overlayExistsValueRef.current !== null) {
        setTransitionRunning(true);
      }

      overlayExistsValueRef.current = isOverlayExists;
    }

    const className = `${styles.noScroll}`;

    const cleanUp = () => {
      if (document.body.classList.contains(className)) {
        document.body.classList.remove(className);
      }
    };

    if (isOverlayExists) {
      const scrollElement = document.getElementById(CONTENT_ID);
      if (scrollElement && scrollElement.scrollTop > 0) {
        scrollElement.scrollTo({ top: 0, behavior: 'smooth' });
      }

      if (!document.body.classList.contains(className)) {
        document.body.classList.add(className);
      }
    } else {
      cleanUp();
    }

    return cleanUp;
  }, [isOverlayExists]);

  return (
    <header {...props} ref={ref} className={styles.header}>
      <div
        className={classes([
          styles.root,
          (isOverlayExists || transitionRunning) && styles.pullToFront,
        ])}
      >
        {company.showMobileHeaderLogo && (
          <Link to="/" data-link="header-logo" className={styles.logo}>
            <img src={company.images.logo.light} alt={company.config.name} />
          </Link>
        )}
        <div className={styles.root__inner}>
          <HeaderOrders onTransitionEnd={onTransitionEnd} />

          <HeaderAddressWrapper />

          <LoyaltyChip />

          {company.showMobileHeaderFavs && (
            <HeaderFavs onTransitionEnd={onTransitionEnd} />
          )}

          {company.showMobileHeaderLanguageChip && (
            <HeaderLanguageChip className="ms-auto" />
          )}
        </div>
        {company.showDeliveryMethodSwitcher && <HeaderDeliverySwitcher />}
        {company.showSubheaderCategories && (
          <>
            <Link
              to="/search"
              data-link="header-search"
              className={styles.searchBtn}
            >
              <Icon type="search-alt" size={20} />
              <span>{t('header:searchProducts')}</span>
            </Link>
            <CategoriesSubheader />
          </>
        )}
      </div>
      {company.showHeaderExpressBanner && <VIPExpressBanner />}
    </header>
  );
});
MobileHeaderCD.displayName = 'MobileHeaderCD';

export default observer(MobileHeaderCD);
